import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useOutletContext, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Tooltip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  FileText, Clock, UserRound, MoreVertical, CheckCircle, RotateCcw, UserCog,
  Calendar,
  ArrowRight,
  Sparkles,
  Plus,
} from "lucide-react";
import { useTheme } from "@mui/material/styles";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { Badge } from "../../components/Badge";
import { DataTable } from "../../components/DataTable";
import { IncidentFiltersModal } from "../../components/IncidentFiltersModal";
import { Button } from "../../components/Button";
import { useAuth } from "../../components/contexts/AuthContext";
import { getData } from "../../components/hooks/getData";
import { AgencyIcon } from "../../components/icons/AgencyIcon";
import { Avatar } from "../../components/Avatar";
import {
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../components/DropdownMenu";
import SuccessPopup from "../../components/SuccessPopup";
import { AgencyPromptsModal } from "../../components/prompts/AgencyPromptsModal";
import { REPORT_PROMPTS } from "../../components/prompts/agencyPrompts";
import { GenerateReportModal } from "./GenerateReportModal";
import { REPORT_STATUS_BADGES, reportsBasePath } from "./reportFormat";
import { settingsPath } from "../Settings/settingsPaths";
import {
  REPORTS_ENDPOINT,
  changeReportStatus,
  fetchReportAiConfig,
  reportAiConfigQueryKey,
  updateReportAiConfig,
} from "../../services/reportsService";
import { getAvatarImageUrl } from "../../utils/avatarUtils";
import { buildDateRangeChipLabel } from "../../utils/dateUtils";
import { isClientReadOnly } from "../../utils/clientAccess";
import { ReadOnlyBadge } from "../../components/ReadOnlyBadge";
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";
import {
  REPORTS_TABLE_TUTORIAL_STEPS,
  REPORTS_TUTORIAL_COMPLETION_DESCRIPTION,
  REPORTS_TUTORIAL_INTRO,
  REPORTS_TUTORIAL_MOCK_ID,
  ReportsTutorialTooltip,
  getReportsTutorialStyles,
} from "../../Tutorials/report/reportstutorial";
import {
  useTutorialsEnabled,
  TutorialCompletionModal,
  TutorialIntroModal,
  TutorialStartButton,
  TUTORIAL_FLOATER_PROPS,
} from "../../Tutorials/index.js";

// === FUNCIONES UTILITARIAS ===

const REPORTS_TUTORIAL_PREFERENCE_KEY = "reports";

/** "Configurar prompts" of the reports page for the agency's own staff: the admin edits, the consultant reads. */
function ReportPromptsButton() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, interceptorReady } = useAuth();
  const [open, setOpen] = useState(false);
  const canEdit = user.role === "agency_admin";

  const { data: config, isLoading, isError, refetch } = useQuery({
    queryKey: reportAiConfigQueryKey(),
    queryFn: () => fetchReportAiConfig(),
    enabled: interceptorReady && open,
  });

  const prompts = config
    ? REPORT_PROMPTS.map((prompt) => ({
        ...prompt,
        settingsKey: prompt.key,
        value: config[prompt.key],
        ownValue: config[prompt.key],
        source: "agency",
      }))
    : [];

  const handleSave = async (promptKey, text) => {
    const data = await updateReportAiConfig(null, { [promptKey]: text });
    queryClient.setQueryData(reportAiConfigQueryKey(), data);
  };

  return (
    <>
      <Button variant="white" onClick={() => setOpen(true)} aria-haspopup="dialog">
        <Sparkles size={16} />
        Configurar prompts
      </Button>
      <AgencyPromptsModal
        open={open}
        onClose={() => setOpen(false)}
        prompts={prompts}
        isLoading={isLoading}
        isError={isError}
        onRetry={() => refetch()}
        canEdit={canEdit}
        onSave={handleSave}
        onOpenSettings={canEdit ? () => navigate(`${settingsPath("prompts")}?prompt=report_generation_prompt`) : undefined}
        description={
          canEdit
            ? "Estas instrucciones guían los textos que la IA redacta en los informes. Los cambios que guardes se aplican a todos los clientes de la agencia."
            : "Estas instrucciones guían los textos que la IA redacta en los informes. Solo lectura: solo un administrador de la agencia puede cambiarlas."
        }
      />
    </>
  );
}

// === APIS DEL BACKEND ===

/**
 * Hook para obtener todos los reportes
 */
const useReports = (interceptorReady, shouldFetch = true, params = {}, refreshDeps = []) => {
  const queryParams = new URLSearchParams();

  if (params.filters?.agency) {
    queryParams.append("agency", params.filters.agency);
  }
  if (params.filters?.client && !params.clientId) {
    queryParams.append("client", params.filters.client);
  }
  if (params.clientId) {
    queryParams.append("client", params.clientId);
  }
  if (params.status && params.status !== "all") {
    queryParams.append("status", params.status);
  }
  if (typeof params.page === "number") {
    queryParams.append("page", (params.page + 1).toString());
  }
  if (params.pageSize) {
    queryParams.append("page_size", params.pageSize.toString());
  }
  if (params.search) {
    queryParams.append("search", params.search);
  }
  if (params.periodStart) {
    queryParams.append("period_start__gte", params.periodStart);
  }
  if (params.periodEnd) {
    queryParams.append("period_end__lte", params.periodEnd);
  }

  const endpoint = `${REPORTS_ENDPOINT}${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;

  return getData(endpoint, interceptorReady && shouldFetch, refreshDeps);
};

const STATUS_CHANGES = {
  approve: {
    toStatus: "approved",
    successMessage: "Informe aprobado y enviado al cliente",
    errorMessage: "No se pudo aprobar el informe",
  },
  revert: {
    toStatus: "generated",
    successMessage: "Informe marcado en revisión",
    errorMessage: "No se pudo revertir el informe",
  },
};

// === COMPONENTES AUXILIARES ===

// === COMPONENTE PRINCIPAL ===

export function Reports() {
  const { user: currentUser, interceptorReady } = useAuth();
  const {
    setPageContext,
    reportsRefreshKey,
    filterRefresh,
    selectedFilter,
    selectedFilterData,
    changeSelectedFilter,
    isMultiClientUser,
    updateUiPreferences,
  } = useOutletContext();
  const theme = useTheme();
  const tutorialsEnabled = useTutorialsEnabled();
  const navigate = useNavigate();
  const location = useLocation();

  // Estados locales
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const searchDebounceTimerRef = useRef(null);
  const lastSearchRef = useRef("");
  const [refresh, setRefresh] = useState(0);

  // Estados para paginación local (frontend)
  const [currentPage, setCurrentPage] = useState(0); // TablePagination usa índice base 0
  const [pageSize, setPageSize] = useState(25);
  const [selectedStatus, setSelectedStatus] = useState("all");

  const [statusStats, setStatusStats] = useState({
    generated: 0,
    approved: 0,
    all: 0,
  });

  // Estados para menú desplegable de acciones
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedReportForMenu, setSelectedReportForMenu] = useState(null);
  const [isProcessing, setIsProcessing] = useState(null);
  const [isHoveringButtons, setIsHoveringButtons] = useState(false);

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [filterModalRefreshKey, setFilterModalRefreshKey] = useState(0);

  // Estados para popup
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [isPopupError, setIsPopupError] = useState(false);
  const [showTutorialSuccessModal, setShowTutorialSuccessModal] = useState(false);
  const [showTutorialIntroModal, setShowTutorialIntroModal] = useState(false);
  const [isFirstTimeTutorialPrompt, setIsFirstTimeTutorialPrompt] = useState(false);
  const [reportsTutorialKey, setReportsTutorialKey] = useState(0);
  const [isReportsTutorialRunning, setIsReportsTutorialRunning] = useState(false);
  const [reportsTutorialStepIndex, setReportsTutorialStepIndex] = useState(0);
  const reportsTutorialAutoPromptedRef = useRef(false);
  const canManageReports = ["superadmin", "agency_admin", "consultant"].includes(currentUser?.role);

  const createEmptyAdvancedFilters = useCallback(
    () => ({
      periodStart: "",
      periodEnd: "",
      client: null,
    }),
    []
  );

  const [appliedAdvancedFilters, setAppliedAdvancedFilters] = useState(() =>
    createEmptyAdvancedFilters()
  );
  const [draftAdvancedFilters, setDraftAdvancedFilters] = useState(() =>
    createEmptyAdvancedFilters()
  );

  const hasAdvancedFiltersApplied = React.useMemo(
    () =>
      Boolean(
        appliedAdvancedFilters.client?.value ||
        appliedAdvancedFilters.periodStart ||
        appliedAdvancedFilters.periodEnd
      ),
    [appliedAdvancedFilters]
  );

  // Manejo de filtros globales (AppLayout)
  const handleClearGlobalFilter = useCallback(() => {
    changeSelectedFilter("global");
  }, [changeSelectedFilter]);

  const globalFilterChip = React.useMemo(() => {
    if (!selectedFilter || !selectedFilterData?.name) {
      return null;
    }
    return {
      key: "global-filter",
      label: selectedFilterData.name,
      onRemove: handleClearGlobalFilter,
    };
  }, [selectedFilter, selectedFilterData, handleClearGlobalFilter]);

  // Chips de filtros avanzados
  const advancedFilterChips = React.useMemo(() => {
    const chips = [];

    if (appliedAdvancedFilters.client) {
      chips.push({
        key: "client",
        label: appliedAdvancedFilters.client.label,
      });
    }
    const { periodStart, periodEnd } = appliedAdvancedFilters;
    if (periodStart || periodEnd) {
      chips.push({
        key: "period",
        label: buildDateRangeChipLabel({ label: "Período", start: periodStart, end: periodEnd }),
      });
    }
    return chips;
  }, [appliedAdvancedFilters]);

  // Combinar todos los chips a mostrar
  const displayedFilterChips = React.useMemo(() => {
    const chips = [];
    if (globalFilterChip) {
      chips.push(globalFilterChip);
    }
    advancedFilterChips.forEach((chip) => {
      chips.push({
        ...chip,
        onRemove: () => handleRemoveAdvancedFilter(chip.key),
      });
    });
    return chips;
  }, [globalFilterChip, advancedFilterChips]);

  // Función para remover un filtro avanzado específico
  const handleRemoveAdvancedFilter = (key) => {
    const clear = (prev) =>
      key === "period" ? { ...prev, periodStart: "", periodEnd: "" } : { ...prev, [key]: null };
    setAppliedAdvancedFilters(clear);
    setDraftAdvancedFilters(clear);
    setCurrentPage(0);
    setRefresh((prev) => prev + 1);
  };

  const shouldFetchFilterClients = isFilterModalOpen && currentUser?.role === "superadmin";
  const { data: filterClientsData, isLoading: isLoadingFilterClients } = getData(
    "clients/options/?ordering=name",
    interceptorReady && shouldFetchFilterClients,
    [filterModalRefreshKey]
  );

  const filterClientOptions = useMemo(() => {
    if (!filterClientsData) return [];
    const items = Array.isArray(filterClientsData)
      ? filterClientsData
      : filterClientsData.results || [];
    return items.map((client) => ({
      value: client.id,
      label: client.name,
    }));
  }, [filterClientsData]);

  // Filtros activos
  const activeFilters = React.useMemo(() => {
    const filters = {};
    if (selectedFilter && selectedFilter !== "global") {
      if (currentUser?.role === "superadmin") {
        filters.agency = selectedFilter;
      } else {
        filters.client = selectedFilter;
      }
    }
    return filters;
  }, [selectedFilter, currentUser?.role]);

  // Debounce para el buscador inline
  useEffect(() => {
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    searchDebounceTimerRef.current = setTimeout(() => {
      const trimmed = searchInput.trim();

      if (trimmed === "" && lastSearchRef.current !== "") {
        lastSearchRef.current = "";
        setSearchTerm("");
        return;
      }

      if (trimmed.length >= 2 && trimmed !== lastSearchRef.current) {
        lastSearchRef.current = trimmed;
        setSearchTerm(trimmed);
      }
    }, 300);

    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [searchInput]);

  // Efecto para limpiar filtros personalizados cuando cambia el selectedFilter del AppLayout
  const previousSelectedFilterRef = React.useRef(selectedFilter);
  useEffect(() => {
    if (previousSelectedFilterRef.current !== selectedFilter) {
      previousSelectedFilterRef.current = selectedFilter;

      // Si hay filtros personalizados activos, limpiarlos
      if (hasAdvancedFiltersApplied) {
        setAppliedAdvancedFilters(createEmptyAdvancedFilters());
        setDraftAdvancedFilters(createEmptyAdvancedFilters());
        setCurrentPage(0);
        setRefresh((prev) => prev + 1);
      }
    }
  }, [selectedFilter, hasAdvancedFiltersApplied, createEmptyAdvancedFilters]);

  // Hook para obtener reportes del backend
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const searchStatusMap = {
    "revision": "generated",
    "revisión": "generated",
    "enviado": "approved",
    "enviados": "approved",
    "aprobado": "approved",
    "aprobados": "approved",
  };

  const mappedStatusFromSearch = searchStatusMap[normalizedSearch];
  const effectiveStatus = selectedStatus !== "all" ? selectedStatus : mappedStatusFromSearch || "all";
  const effectiveSearch = selectedStatus !== "all" || mappedStatusFromSearch ? "" : searchTerm;

  const advancedClientId = appliedAdvancedFilters.client?.value || null;
  const { periodStart, periodEnd } = appliedAdvancedFilters;

  const { data: backendReports, isLoading: isLoadingReports } = useReports(
    interceptorReady,
    true,
    {
      filters: activeFilters,
      clientId: advancedClientId,
      periodStart,
      periodEnd,
      status: effectiveStatus,
      page: currentPage,
      pageSize,
      search: effectiveSearch,
    },
    [
      reportsRefreshKey,
      refresh,
      filterRefresh,
      activeFilters,
      advancedClientId,
      periodStart,
      periodEnd,
      currentPage,
      pageSize,
      effectiveStatus,
      effectiveSearch,
    ]
  );


  // Procesar reportes del backend
  const [allReports, setAllReports] = useState([]);
  const [totalReports, setTotalReports] = useState(0);

  useEffect(() => {
    if (backendReports) {
      setAllReports(backendReports.results);
      setTotalReports(backendReports.count);
      setStatusStats(backendReports.status_stats);
    }
  }, [backendReports]);

  // Funciones para manejar cambios de página y tamaño de página
  const handleChangePage = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (nextPageSize) => {
    setPageSize(nextPageSize);
    setCurrentPage(0); // Resetear a la primera página
  };

  // Resetear página cuando cambie la búsqueda
  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(0);
  }, [activeFilters, selectedStatus, appliedAdvancedFilters]);

  // Configurar el contexto de la página
  useEffect(() => {
    if (currentUser && setPageContext) {
      // Generar breadcrumb según el rol y el cliente seleccionado
      let breadcrumbItems = null;

      if (currentUser.role === "superadmin") {
        // Superadmin: "Vista Global" > Agencia: (si hay agencia en applayout)
        if (selectedFilter && selectedFilterData) {
          breadcrumbItems = [
            {
              label: "Vista Global",
              onClick: () => changeSelectedFilter("global"),
            },
            { label: `Agencia: ${selectedFilterData.name}` },
          ];
        } else {
          breadcrumbItems = [{ label: "Vista Global" }];
        }
      } else if (
        currentUser.role === "agency_admin" ||
        currentUser.role === "consultant"
      ) {
        // Agency_admin y consultant: "Vista Global" > Cliente: (si hay en applayout)
        if (selectedFilter && selectedFilterData) {
          breadcrumbItems = [
            {
              label: "Vista Global",
              onClick: () => changeSelectedFilter("global"),
            },
            { label: `Cliente: ${selectedFilterData.name}` },
          ];
        } else {
          breadcrumbItems = [{ label: "Vista Global" }];
        }
      } else if (currentUser.role === "client") {
        // Cliente: depende si es multicliente
        if (isMultiClientUser) {
          // Multicliente: igual que agency_admin pero con "Empresa:"
          if (selectedFilter && selectedFilterData) {
            breadcrumbItems = [
              {
                label: "Vista Global",
                onClick: () => changeSelectedFilter("global"),
              },
              { label: `Empresa: ${selectedFilterData.name}` },
            ];
          } else {
            breadcrumbItems = [{ label: "Vista Global" }];
          }
        }

      }

      const showTutorialButton = tutorialsEnabled && canManageReports;
      const rightContent = canManageReports ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {currentUser.role === "superadmin" ? (
            <Button
              variant="white"
              onClick={() => navigate(`${settingsPath("prompts")}?prompt=report_generation_prompt`)}
            >
              <Sparkles size={16} />
              Configurar prompts
            </Button>
          ) : (
            <ReportPromptsButton />
          )}
          <Button onClick={() => setIsGenerateModalOpen(true)} data-tutorial="reports-generate">
            <Plus size={16} />
            Generar informe
          </Button>
        </Box>
      ) : null;

      setPageContext({
        title: "Informes",
        showTitle: true,
        titleRightContent: showTutorialButton ? (
          <TutorialStartButton
            onClick={() => {
              setIsFirstTimeTutorialPrompt(false);
              setShowTutorialIntroModal(true);
            }}
          />
        ) : null,
        rightContent,
        breadcrumbItems,
      });
    }
  }, [
    canManageReports,
    changeSelectedFilter,
    currentUser,
    isMultiClientUser,
    navigate,
    selectedFilter,
    selectedFilterData,
    setPageContext,
    tutorialsEnabled,
  ]);

  useEffect(() => {
    if (!location.state?.reportsTutorialCompleted) return;

    setShowTutorialSuccessModal(true);
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
  }, [
    location.pathname,
    location.search,
    location.state,
    navigate,
  ]);

  useEffect(() => {
    if (!location.state?.reportsTutorialGoBack) return;

    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
    setReportsTutorialStepIndex(REPORTS_TABLE_TUTORIAL_STEPS.length - 1);
    setReportsTutorialKey((current) => current + 1);
    requestAnimationFrame(() => setIsReportsTutorialRunning(true));
  }, [
    location.pathname,
    location.search,
    location.state,
    navigate,
  ]);

  // Verificar si el usuario es cliente
  const isClient = currentUser?.role === "client";
  const showClientColumn = !isClient || isMultiClientUser;
  const basePath = reportsBasePath(currentUser?.role);

  const reportsTutorialState =
    currentUser?.ui_preferences?.tutorials?.[REPORTS_TUTORIAL_PREFERENCE_KEY] || {};

  const markReportsTutorialVisited = useCallback(async () => {
    if (reportsTutorialState.visited_at) {
      return;
    }

    await updateUiPreferences({
      tutorials: {
        [REPORTS_TUTORIAL_PREFERENCE_KEY]: {
          visited_at: new Date().toISOString(),
        },
      },
    });
  }, [
    reportsTutorialState.visited_at,
    updateUiPreferences,
  ]);

  const handleCloseReportsTutorialIntro = useCallback(async () => {
    setShowTutorialIntroModal(false);
    setIsFirstTimeTutorialPrompt(false);

    try {
      await markReportsTutorialVisited();
    } catch (error) {
      console.error("Error updating reports tutorial visited state:", error);
    }
  }, [markReportsTutorialVisited]);

  useEffect(() => {
    if (!tutorialsEnabled || !canManageReports || !currentUser?.id || reportsTutorialAutoPromptedRef.current) {
      return;
    }

    if (reportsTutorialState.visited_at) {
      return;
    }

    reportsTutorialAutoPromptedRef.current = true;
    setIsFirstTimeTutorialPrompt(true);
    setShowTutorialIntroModal(true);
  }, [
    canManageReports,
    currentUser?.id,
    reportsTutorialState.visited_at,
    tutorialsEnabled,
  ]);

  const showStatusColumn = !isClient && !isMultiClientUser;
  const showApprovedByColumn = !isClient;
  const showActionsColumn = canManageReports && !isClient;
  // Mostrar loading
  const showLoading = isLoadingReports;

  // Funciones para manejar el menú desplegable
  const handleMenuOpen = (event, report) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedReportForMenu(report);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedReportForMenu(null);
  };

  const menuOpen = Boolean(menuAnchorEl);

  const updateStatusStats = useCallback((fromStatus, toStatus) => {
    if (!fromStatus || !toStatus || fromStatus === toStatus) return;
    setStatusStats((prev) => {
      const next = { ...prev };
      if (typeof next[fromStatus] === "number") {
        next[fromStatus] = Math.max(0, next[fromStatus] - 1);
      }
      if (typeof next[toStatus] === "number") {
        next[toStatus] = next[toStatus] + 1;
      }
      return next;
    });
  }, []);

  const handleStatusChange = async (reportId, action) => {
    const { toStatus, successMessage, errorMessage } = STATUS_CHANGES[action];
    try {
      setIsProcessing(reportId);
      const previousReport = allReports.find((report) => report.id === reportId);
      const updatedReport = await changeReportStatus(reportId, action);

      setAllReports((prev) =>
        prev.map((report) => (report.id === reportId ? { ...report, ...updatedReport } : report))
      );
      updateStatusStats(previousReport?.status, toStatus);

      setPopupMessage(successMessage);
      setIsPopupError(false);
      setShowSuccessPopup(true);
    } catch (error) {
      console.error(`Error en ${action} del informe:`, error);
      setPopupMessage(error.response?.data?.detail || errorMessage);
      setIsPopupError(true);
      setShowSuccessPopup(true);
    } finally {
      setIsProcessing(null);
      handleMenuClose();
    }
  };

  const startReportsTutorial = () => {
    setShowTutorialIntroModal(false);
    setIsFirstTimeTutorialPrompt(false);
    setIsReportsTutorialRunning(false);

    markReportsTutorialVisited()
      .catch((error) => {
        console.error("Error updating reports tutorial visited state:", error);
      })
      .finally(() => {
        setReportsTutorialStepIndex(0);
        setReportsTutorialKey((current) => current + 1);
        requestAnimationFrame(() => {
          setIsReportsTutorialRunning(true);
        });
      });
  };

  const openAdvancedFiltersModal = () => {
    setDraftAdvancedFilters({ ...appliedAdvancedFilters });
    setIsFilterModalOpen(true);
    setFilterModalRefreshKey((prev) => prev + 1);
  };

  const captionTextSx = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: { xs: "0.7rem", md: "0.75rem" },
  };

  const inlineCellSx = {
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    minWidth: 0,
    width: "fit-content",
  };

  const reportColumns = [
    {
      key: "client",
      header: "Cliente",
      hidden: !showClientColumn,
      minWidth: { xs: "120px", md: "150px" },
      render: (report) => (
        <Tooltip title={report.client_name || "-"} arrow>
          <Box sx={inlineCellSx}>
            <Avatar
              size="sm"
              name={report.client_name || "Cliente"}
              src={getAvatarImageUrl(report.client_logo) || undefined}
            />
            <Typography
              variant="caption"
              color="text.primary"
              sx={{ ...captionTextSx, fontWeight: 500 }}
            >
              {report.client_name || "-"}
            </Typography>
            {isClientReadOnly(currentUser, report.client_id) && <ReadOnlyBadge />}
          </Box>
        </Tooltip>
      ),
    },
    {
      key: "agency",
      header: "Agencia",
      hidden: currentUser?.role !== "superadmin",
      minWidth: { xs: "100px", md: "120px" },
      render: (report) => (
        <Tooltip title={report.agency_name || "-"} arrow>
          <Box sx={inlineCellSx}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 24,
                width: 24,
                flexShrink: 0,
              }}
            >
              <AgencyIcon size={20} color={theme.palette.text.secondary} />
            </Box>
            <Typography variant="caption" color="text.primary" sx={captionTextSx}>
              {report.agency_name || "-"}
            </Typography>
          </Box>
        </Tooltip>
      ),
    },
    {
      key: "period",
      header: "Período",
      minWidth: { xs: "100px", md: "120px" },
      render: (report) => (
        <Tooltip title={report.period_label} arrow>
          <Box sx={inlineCellSx}>
            <Clock size={12} color={theme.palette.text.secondary} />
            <Typography variant="caption" color="text.primary" sx={captionTextSx}>
              {report.period_label}
            </Typography>
          </Box>
        </Tooltip>
      ),
    },
    {
      key: "details",
      header: "",
      hidden: !isClient,
      align: "right",
      width: "1%",
      render: (report) => (
        <Box
          className="hover-button"
          component="button"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`${basePath}/${report.id}`);
          }}
          sx={{
            background: "none",
            border: "none",
            padding: 0,
            margin: 0,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            color: theme.palette.text.primary,
            textDecoration: "underline",
            textUnderlineOffset: "2px",
            fontSize: { xs: "0.75rem", md: "0.875rem" },
            fontFamily: theme.typography.fontFamily,
            fontWeight: 500,
            whiteSpace: "nowrap",
            transition: "all 0.2s ease",
            "&:hover": {
              color: theme.palette.primary.main,
            },
            "&:active": {
              color: theme.palette.primary.main,
              animation: "pulseClick 0.4s ease",
              textDecorationColor: theme.palette.primary.main,
            },
            "@keyframes pulseClick": {
              "0%": {
                color: theme.palette.text.primary,
              },
              "50%": {
                color: theme.palette.primary.main,
                transform: "scale(1.05)",
              },
              "100%": {
                color: theme.palette.primary.main,
                transform: "scale(1)",
              },
            },
          }}
        >
          Ver detalles
          <ArrowRight size={14} />
        </Box>
      ),
    },
    {
      key: "status",
      header: <span data-tutorial="reports-status-column">Estado</span>,
      hidden: !showStatusColumn,
      minWidth: { xs: "80px", md: "100px" },
      render: (report) => (
        <Badge variant="outline" color={REPORT_STATUS_BADGES[report.status].color}>
          {REPORT_STATUS_BADGES[report.status].label}
        </Badge>
      ),
    },
    {
      key: "approvedBy",
      header: "Aprobado por",
      hidden: !showApprovedByColumn,
      minWidth: { xs: "100px", md: "120px" },
      render: (report) =>
        report.approved_by_name ? (
          <Tooltip title={report.approved_by_name} arrow>
            <Box sx={inlineCellSx}>
              <UserCog size={12} color={theme.palette.text.secondary} />
              <Typography variant="caption" color="text.primary" sx={captionTextSx}>
                {report.approved_by_name}
              </Typography>
            </Box>
          </Tooltip>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
            -
          </Typography>
        ),
    },
    {
      key: "actions",
      header: <span data-tutorial="reports-actions-column">Acciones</span>,
      hidden: !showActionsColumn,
      align: "center",
      minWidth: { xs: "60px", md: "80px" },
      render: (report) => {
        const isCurrentlyProcessing = isProcessing === report.id;
        return (
          <>
            <IconButton
              size="small"
              onClick={(e) => handleMenuOpen(e, report)}
              onMouseEnter={() => setIsHoveringButtons(true)}
              onMouseLeave={() => setIsHoveringButtons(false)}
              sx={{
                width: "32px",
                height: "32px",
                padding: 0,
                flexShrink: 0,
              }}
            >
              <MoreVertical size={16} />
            </IconButton>
            {menuOpen && selectedReportForMenu?.id === report.id && (
              <DropdownMenuContent
                anchorEl={menuAnchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                align="end"
                onClick={(e) => e.stopPropagation()}
              >
                {report.status === "generated" && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStatusChange(report.id, "approve");
                    }}
                    disabled={isCurrentlyProcessing}
                  >
                    {isCurrentlyProcessing ? (
                      <CircularProgress size={12} />
                    ) : (
                      <CheckCircle size={16} />
                    )}
                    Aprobar y enviar
                  </DropdownMenuItem>
                )}
                {report.status === "approved" && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStatusChange(report.id, "revert");
                    }}
                    disabled={isCurrentlyProcessing}
                  >
                    {isCurrentlyProcessing ? (
                      <CircularProgress size={12} />
                    ) : (
                      <RotateCcw size={16} />
                    )}
                    Marcar en revisión
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            )}
          </>
        );
      },
    },
  ];

  // Los clientes no tienen buscador ni tabs: solo el botón de filtros avanzados.
  const reportsToolbarProps = isClient
    ? {
        onFilterClick: openAdvancedFiltersModal,
        activeFiltersCount: hasAdvancedFiltersApplied ? advancedFilterChips.length : 0,
        filterTooltip: "Filtros avanzados",
      }
    : {
        searchValue: searchInput,
        onSearchChange: setSearchInput,
        searchPlaceholder: "Buscar reportes...",
        tabs: [
          { value: "all", label: "Todos", count: statusStats.all },
          { value: "generated", label: "Revisión", count: statusStats.generated },
          { value: "approved", label: "Enviados", count: statusStats.approved },
        ],
        activeTab: selectedStatus,
        onTabChange: (value) => {
          setSelectedStatus(value);
          setCurrentPage(0);
          setRefresh((prev) => prev + 1);
        },
        filterChips: displayedFilterChips.map((chip) => ({
          ...chip,
          key: `${chip.key}-${chip.label}`,
        })),
        onFilterClick: openAdvancedFiltersModal,
        filterTooltip: "Editar filtros",
      };

  return (
    <Box data-tutorial="reports-table" sx={{ width: "100%", maxWidth: "100%" }}>
      <DataTable
        {...reportsToolbarProps}
        columns={reportColumns}
        rows={allReports}
        loading={showLoading}
        emptyIcon={FileText}
        emptyMessage={
          searchTerm
            ? "No se encontraron reportes con los filtros"
            : isClient
              ? "Aún no tienes informes"
              : "Aún no hay informes de clientes"
        }
        onRowClick={(report) => navigate(`${basePath}/${report.id}`)}
        getRowSx={() => ({
          transition: "background-color 0.2s ease",
          ...(isHoveringButtons && { "&:hover": { backgroundColor: "transparent" } }),
        })}
        tableMinWidth={350}
        maxHeight="calc(100vh - 290px)"
        minHeight="calc(100vh - 290px)"
        page={currentPage}
        rowsPerPage={pageSize}
        totalCount={totalReports}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Joyride
        floaterProps={TUTORIAL_FLOATER_PROPS}
        key={`reports-tutorial-${reportsTutorialKey}`}
        steps={REPORTS_TABLE_TUTORIAL_STEPS}
        run={tutorialsEnabled && isReportsTutorialRunning}
        stepIndex={reportsTutorialStepIndex}
        continuous
        showSkipButton={false}
        showProgress={false}
        scrollToFirstStep
        disableOverlayClose
        disableCloseOnEsc
        disableScrolling
        disableScrollParentFix
        locale={{
          close: "Cerrar",
          last: "Siguiente",
          next: "Siguiente",
        }}
        tooltipComponent={ReportsTutorialTooltip}
        styles={getReportsTutorialStyles(theme, 10000)}
        callback={({ action, index, status, type }) => {
          if (status === STATUS.SKIPPED) {
            setIsReportsTutorialRunning(false);
            return;
          }

          if (type !== EVENTS.STEP_AFTER && type !== EVENTS.TARGET_NOT_FOUND) {
            return;
          }

          const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
          if (nextIndex >= REPORTS_TABLE_TUTORIAL_STEPS.length) {
            // El último paso de la tabla abre el informe de ejemplo.
            setIsReportsTutorialRunning(false);
            navigate(`${basePath}/${REPORTS_TUTORIAL_MOCK_ID}`);
            return;
          }
          setReportsTutorialStepIndex(Math.max(nextIndex, 0));
        }}
      />

      <TutorialCompletionModal
        open={tutorialsEnabled && showTutorialSuccessModal}
        onClose={() => setShowTutorialSuccessModal(false)}
        description={REPORTS_TUTORIAL_COMPLETION_DESCRIPTION}
      />

      <TutorialIntroModal
        open={tutorialsEnabled && showTutorialIntroModal}
        onClose={handleCloseReportsTutorialIntro}
        onConfirm={startReportsTutorial}
        isFirstTimePrompt={isFirstTimeTutorialPrompt}
        firstTimeBadge = "Ejemplo guiado"
        {...REPORTS_TUTORIAL_INTRO}
      />

      {isGenerateModalOpen && (
        <GenerateReportModal
          onClose={() => setIsGenerateModalOpen(false)}
          basePath={basePath}
          defaultClientId={currentUser.role === "superadmin" ? undefined : activeFilters.client}
          agencyId={currentUser.role === "superadmin" ? activeFilters.agency : undefined}
        />
      )}

      {/* Popup de éxito/error */}
      <SuccessPopup
        isOpen={showSuccessPopup}
        onClose={() => setShowSuccessPopup(false)}
        message={popupMessage}
        error={isPopupError}
      />

      <IncidentFiltersModal
        open={isFilterModalOpen}
        onClose={() => {
          setIsFilterModalOpen(false);
          setDraftAdvancedFilters({ ...appliedAdvancedFilters });
        }}
        title="Filtros avanzados"
        filterConfigs={[
          {
            key: "period",
            type: "date-range",
            ui: "calendar-range",
            startKey: "periodStart",
            endKey: "periodEnd",
            title: "Período",
            description: "Informes cuyo periodo empieza y termina dentro de estas fechas",
            icon: Calendar,
          },
          {
            key: "client",
            title: "Cliente",
            description: "Selecciona un cliente específico",
            icon: UserRound,
            options: filterClientOptions,
            loading: isLoadingFilterClients,
            showForRoles: ["superadmin"],
            role: currentUser?.role,
          },
        ]}
        draftFilters={draftAdvancedFilters}
        onChange={(field, value) => {
          setDraftAdvancedFilters((prev) => ({
            ...prev,
            [field]: value,
          }));
        }}
        onApply={() => {
          setAppliedAdvancedFilters({ ...draftAdvancedFilters });
          setIsFilterModalOpen(false);
          setCurrentPage(0);
          setRefresh((prev) => prev + 1);
        }}
        onClearAll={() => setDraftAdvancedFilters(createEmptyAdvancedFilters())}
      />
    </Box>
  );
}
